import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import dayjs from 'dayjs';

import { parseNaturalTask, createTask } from '@src/api/tasks';

const PRIORITY_VALUES = ['low', 'medium', 'high', 'critical'];

const PHASE_SPEAK = 'speak';
const PHASE_PREVIEW = 'preview';

function QuickVoiceTaskDialog({ open, onClose, onCreated, clients, assignees }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // ---- transcript / mic state -----------------------------------------
  const [transcript, setTranscript] = useState('');
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const voiceFinalsRef = useRef('');

  // ---- phase / preview state ------------------------------------------
  const [phase, setPhase] = useState(PHASE_SPEAK);
  const [parsing, setParsing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null); // {title, client_id, assignee_id, priority, due_date, notes, ai_reasoning, confidence}

  const supported = typeof window !== 'undefined'
    && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  // ---- voice handlers --------------------------------------------------
  const startListening = useCallback(() => {
    if (!supported) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    voiceFinalsRef.current = '';

    recognition.onresult = (event) => {
      let interimPart = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          const trimmed = text.trim();
          if (trimmed) {
            const sep = voiceFinalsRef.current && !/\s$/.test(voiceFinalsRef.current) ? ' ' : '';
            voiceFinalsRef.current = `${voiceFinalsRef.current}${sep}${trimmed}`;
          }
        } else {
          interimPart += text;
        }
      }
      const finals = voiceFinalsRef.current;
      const sep = finals && interimPart && !/\s$/.test(finals) ? ' ' : '';
      setTranscript(`${finals}${sep}${interimPart}`);
    };
    recognition.onerror = (event) => {
      console.warn('Voice recognition error:', event.error);
      setListening(false);
    };
    recognition.onend = () => {
      setTranscript(voiceFinalsRef.current);
      setListening(false);
    };
    recognitionRef.current = recognition;
    setListening(true);
    try {
      recognition.start();
    } catch (err) {
      console.warn('Could not start voice recognition:', err);
      setListening(false);
    }
  }, [supported]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) { /* ignore */ }
    }
    setListening(false);
  }, []);

  // Reset everything when the dialog opens / auto-start the mic.
  useEffect(() => {
    if (open && supported) {
      setTranscript('');
      setPhase(PHASE_SPEAK);
      setPreview(null);
      setError('');
      voiceFinalsRef.current = '';
      const t = setTimeout(startListening, 150);
      return () => clearTimeout(t);
    }
    if (!open) {
      stopListening();
    }
    return undefined;
  }, [open, supported, startListening, stopListening]);

  useEffect(() => () => stopListening(), [stopListening]);

  // ---- transitions -----------------------------------------------------
  const handleParseAndPreview = () => {
    const text = (transcript || '').trim();
    if (!text) return;
    stopListening();
    setParsing(true);
    setError('');
    parseNaturalTask({ transcript: text, create: false })
      .then((response) => {
        const parsed = response.data?.parsed;
        if (!parsed) {
          setError('AI did not return a parseable task.');
          return;
        }
        setPreview(parsed);
        setPhase(PHASE_PREVIEW);
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail || 'Unable to parse the task.';
        const fallback = err?.response?.data?.parsed;
        if (fallback) {
          // Partial parse — show the preview anyway and let the user fix it.
          setPreview(fallback);
          setPhase(PHASE_PREVIEW);
          setError(detail);
        } else {
          setError(detail);
        }
      })
      .finally(() => setParsing(false));
  };

  const handleBackToDictate = () => {
    setPhase(PHASE_SPEAK);
    setError('');
    if (supported) {
      // resume dictation; existing transcript stays in voiceFinalsRef
      voiceFinalsRef.current = transcript || '';
      startListening();
    }
  };

  const updatePreview = (field, value) => {
    setPreview((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleCreate = () => {
    if (!preview?.title) {
      setError('Title is required.');
      return;
    }
    setCreating(true);
    setError('');
    createTask({
      title: preview.title,
      client: preview.client_id || null,
      assignee: preview.assignee_id || null,
      due_date: preview.due_date || null,
      priority: preview.priority || 'medium',
      notes: preview.notes || '',
      description: transcript
        ? `Voice-created from: ${transcript}`
        : '',
    })
      .then((response) => {
        const task = response.data;
        if (onCreated) onCreated(task);
        onClose();
      })
      .catch((err) => {
        console.error('Create failed', err);
        const detail = err?.response?.data?.detail
          || (err?.response?.data && JSON.stringify(err.response.data))
          || 'Unable to create the task.';
        setError(detail);
      })
      .finally(() => setCreating(false));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (phase === PHASE_SPEAK) handleParseAndPreview();
      else handleCreate();
    }
  };

  // ---- derived options for autocompletes ------------------------------
  const clientOption = (preview && preview.client_id)
    ? (clients || []).find((c) => c.id === preview.client_id) || null
    : null;
  const assigneeOption = (preview && preview.assignee_id)
    ? (assignees || []).find((u) => u.id === preview.assignee_id) || null
    : null;

  // ---- render ----------------------------------------------------------
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      onKeyDown={handleKeyDown}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoAwesomeIcon color="primary" />
          <span>{phase === PHASE_SPEAK ? 'Quick task — speak it' : 'Preview & confirm'}</span>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {phase === PHASE_SPEAK
            ? "Say what needs doing, who it's for, and when."
            : 'AI took its best guess — tweak anything before saving.'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {!supported && phase === PHASE_SPEAK ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Voice input isn't supported in this browser. Type the task below.
          </Alert>
        ) : null}

        {phase === PHASE_SPEAK ? (
          <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <IconButton
                color={listening ? 'error' : 'primary'}
                onClick={listening ? stopListening : startListening}
                sx={{
                  bgcolor: listening ? 'error.lighter' : 'primary.lighter',
                  '&:hover': { bgcolor: listening ? 'error.light' : 'primary.light' },
                }}
                disabled={!supported || parsing}
              >
                {listening ? <StopIcon /> : <MicIcon />}
              </IconButton>
              {listening
                ? <Chip size="small" label="Listening…" color="error" />
                : <Typography variant="caption" color="text.secondary">
                    {supported ? 'Click the mic to dictate.' : ''}
                  </Typography>}
              <Box sx={{ flexGrow: 1 }} />
            </Stack>
            <TextField
              value={transcript}
              onChange={(event) => {
                setTranscript(event.target.value);
                voiceFinalsRef.current = event.target.value;
              }}
              autoFocus
              fullWidth
              multiline
              minRows={5}
              maxRows={12}
              placeholder={listening
                ? 'Listening…'
                : "e.g. \"Follow up with Acme on May invoice tomorrow, high priority\""}
              disabled={parsing}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              ⌘+Enter to preview.
            </Typography>
          </>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={preview?.title || ''}
              onChange={(e) => updatePreview('title', e.target.value)}
              fullWidth
              autoFocus
            />

            <Autocomplete
              options={clients || []}
              value={clientOption}
              getOptionLabel={(opt) => (opt && opt.name) || ''}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              onChange={(_, opt) => updatePreview('client_id', opt ? opt.id : null)}
              renderInput={(params) => (
                <TextField {...params} label="Client (optional — leave empty for internal / cross-client)" />
              )}
            />

            <Autocomplete
              options={assignees || []}
              value={assigneeOption}
              getOptionLabel={(opt) => opt?.full_name || opt?.email || ''}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              onChange={(_, opt) => updatePreview('assignee_id', opt ? opt.id : null)}
              renderInput={(params) => <TextField {...params} label="Assignee (optional)" />}
            />

            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Priority
                </Typography>
                <ToggleButtonGroup
                  value={preview?.priority || 'medium'}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && updatePreview('priority', v)}
                  fullWidth
                >
                  {PRIORITY_VALUES.map((p) => (
                    <ToggleButton key={p} value={p} sx={{ textTransform: 'capitalize' }}>
                      {p}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DatePicker
                  label="Due date"
                  value={preview?.due_date ? dayjs(preview.due_date) : null}
                  onChange={(v) => updatePreview('due_date', v ? v.format('YYYY-MM-DD') : null)}
                  slotProps={{ textField: { fullWidth: true }, field: { clearable: true } }}
                />
              </Box>
            </Stack>

            <TextField
              label="Notes"
              value={preview?.notes || ''}
              onChange={(e) => updatePreview('notes', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
            />

            {(preview?.ai_reasoning || preview?.confidence != null) ? (
              <Box sx={{
                p: 1.25,
                borderRadius: 1,
                bgcolor: 'action.hover',
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
              }}>
                <AutoAwesomeIcon fontSize="small" color="primary" sx={{ mt: 0.25 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      AI reasoning
                    </Typography>
                    {preview.confidence != null ? (
                      <Chip size="small" variant="outlined" label={`${preview.confidence}% confidence`} />
                    ) : null}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {preview.ai_reasoning || '—'}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            {transcript ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                You said: "{transcript}"
              </Typography>
            ) : null}
          </Stack>
        )}

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : null}
      </DialogContent>

      <DialogActions>
        {phase === PHASE_SPEAK ? (
          <>
            <Button onClick={onClose} disabled={parsing}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleParseAndPreview}
              disabled={!transcript.trim() || parsing}
              startIcon={parsing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            >
              {parsing ? 'Parsing…' : 'Preview'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleBackToDictate} startIcon={<ArrowBackIcon />} disabled={creating}>
              Back to dictate
            </Button>
            <Button onClick={onClose} disabled={creating}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating || !preview?.title}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            >
              {creating ? 'Creating…' : 'Create task'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

QuickVoiceTaskDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  clients: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
  })),
  assignees: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    full_name: PropTypes.string,
    email: PropTypes.string,
  })),
};

QuickVoiceTaskDialog.defaultProps = {
  onCreated: undefined,
  clients: [],
  assignees: [],
};

export default QuickVoiceTaskDialog;
